import { Component, Inject } from '@angular/core';

import { translate } from '@jsverse/transloco';
import { DropdownObject, EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { MatDialogRef, MAT_DIALOG_DATA } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

// eslint-disable-next-line
export interface DownloadAsDialogOptions {
  recordCount?: number;
  sort?: boolean;
  filename?: string;
  format: 'csv' | 'xlsx';
}

export interface DownloadAsDialogResult {
  fileName: string;
  recordCount: number;
  sortOrder?: string;
  taxonomy: boolean;
}

const limitsByFormat = {
  xlsx: {
    label: 'Excel',
    limit: 1000000,
  },
  csv: {
    label: 'CSV',
    limit: 1000,
  },
};

@EnableClassTelemetry()
@Component({
  selector: 'app-download-as-dialog',
  templateUrl: './download-as-dialog.component.html',
  styleUrls: ['./download-as-dialog.component.scss'],
  standalone: false,
})
export class DownloadAsDialogComponent {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  fileNameStatus: ErrorObject | null = null;
  recordCountStatus: ErrorObject | null = null;

  sort: boolean;
  sortOrders: DropdownObject[] = [
    { title: translate('download-as-dialog.random'), id: '_random' },
    { title: translate('download-as-dialog.created-desc'), id: 'createdAt:DESC' },
    { title: translate('download-as-dialog.created-asc'), id: 'createdAt:ASC' },
  ];

  result: DownloadAsDialogResult = {
    fileName: '',
    recordCount: 0,
    sortOrder: '_random',
    taxonomy: false,
  };

  fileFormatText: string;
  limit: number;

  constructor(
    private dialogRef: MatDialogRef<DownloadAsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private options: DownloadAsDialogOptions
  ) {
    this.sort = options.sort;

    limitsByFormat.xlsx.label = translate('download-as-dialog.excel');
    limitsByFormat.csv.label = translate('download-as-dialog.csv');
    if (options.recordCount) {
      this.result.recordCount = this.limit =
        options.recordCount <= limitsByFormat[this.options.format].limit
          ? options.recordCount
          : limitsByFormat[this.options.format].limit;
    } else {
      this.result.recordCount = this.limit = limitsByFormat[this.options.format].limit;
    }

    if (options.filename) {
      this.result.fileName = options.filename;
    }

    this.fileFormatText = '.' + this.options.format;
  }

  public onValidateFileName() {
    if (this.result.fileName.length < 1 || this.result.fileName.trim().length < 1) {
      this.fileNameStatus = {
        state: EnumErrorStates.ERROR,
        text: translate('download-as-dialog.error-required'),
      };
    } else {
      this.fileNameStatus = null;
    }
  }

  public onValidateRecordCount() {
    if (!this.result.recordCount) {
      this.recordCountStatus = {
        state: EnumErrorStates.ERROR,
        text: translate('download-as-dialog.error-required'),
      };
    } else if (this.result.recordCount <= 0 || this.result.recordCount > this.limit) {
      this.recordCountStatus = {
        state: EnumErrorStates.ERROR,
        text: translate('download-as-dialog.error-record-count-limit', { min: 0, max: this.limit }),
      };
    } else if (this.result.recordCount !== Math.trunc(this.result.recordCount)) {
      this.recordCountStatus = {
        state: EnumErrorStates.ERROR,
        text: translate('download-as-dialog.error-record-count-invalid'),
      };
    } else {
      this.recordCountStatus = null;
    }
  }

  public get hasError(): boolean {
    return (
      !this.result.fileName ||
      this.result.fileName.trim().length < 1 ||
      this.recordCountStatus != null ||
      !this.result.sortOrder
    );
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public async confirm(): Promise<void> {
    const res = { ...this.result };
    res.fileName = this.result.fileName + this.fileFormatText;

    this.dialogRef.close(res);
  }

  public get label() {
    return limitsByFormat[this.options.format].label;
  }
}
